/**
 * Identity capture: sharp close face + blurred background.
 * Uses MediaPipe landmark ellipse so secondary/distant faces are suppressed.
 */

const FOREHEAD = 10;
const CHIN = 152;
const LEFT_CHEEK = 234;
const RIGHT_CHEEK = 454;

function landmarkXY(landmarks, index, width, height) {
  const lm = landmarks?.[index];
  if (!lm) return null;
  const x = typeof lm.x === 'number' ? lm.x : (Array.isArray(lm) ? lm[0] : null);
  const y = typeof lm.y === 'number' ? lm.y : (Array.isArray(lm) ? lm[1] : null);
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  return { x: x * width, y: y * height };
}

/**
 * Ellipse covering the face from cheek/forehead/chin landmarks, with padding.
 * @returns {{ cx: number, cy: number, rx: number, ry: number } | null}
 */
export function faceEllipseFromLandmarks(landmarks, width, height, pad = 0.18) {
  if (!landmarks?.length || !width || !height) return null;

  const forehead = landmarkXY(landmarks, FOREHEAD, width, height);
  const chin = landmarkXY(landmarks, CHIN, width, height);
  const leftCheek = landmarkXY(landmarks, LEFT_CHEEK, width, height);
  const rightCheek = landmarkXY(landmarks, RIGHT_CHEEK, width, height);
  if (!forehead || !chin || !leftCheek || !rightCheek) return null;

  const cx = (leftCheek.x + rightCheek.x) / 2;
  const cy = (forehead.y + chin.y) / 2;
  let rx = Math.abs(rightCheek.x - leftCheek.x) / 2;
  let ry = Math.abs(chin.y - forehead.y) / 2;
  rx *= 1 + pad;
  ry *= 1 + pad * 1.15;
  rx = Math.max(rx, width * 0.12);
  ry = Math.max(ry, height * 0.16);
  return { cx, cy, rx, ry };
}

function makeSoftFaceMask(width, height, ellipse) {
  const mask = document.createElement('canvas');
  mask.width = width;
  mask.height = height;
  const mctx = mask.getContext('2d');
  if (!mctx) return null;
  mctx.fillStyle = '#000';
  mctx.fillRect(0, 0, width, height);
  const soft = mctx.createRadialGradient(
    ellipse.cx,
    ellipse.cy,
    Math.min(ellipse.rx, ellipse.ry) * 0.65,
    ellipse.cx,
    ellipse.cy,
    Math.max(ellipse.rx, ellipse.ry),
  );
  soft.addColorStop(0, '#fff');
  soft.addColorStop(0.82, '#fff');
  soft.addColorStop(1, 'rgba(255,255,255,0)');
  mctx.fillStyle = soft;
  mctx.beginPath();
  mctx.ellipse(ellipse.cx, ellipse.cy, ellipse.rx, ellipse.ry, 0, 0, Math.PI * 2);
  mctx.fill();
  return mask;
}

/**
 * Draw video onto canvas with background blurred outside the face ellipse.
 * Falls back to a full-frame sharp draw if landmarks/mask fail.
 *
 * @returns {{ width: number, height: number }}
 */
export function drawIdentityCaptureWithBlur(canvas, video, opts = {}) {
  const maxSide = opts.maxSide ?? 720;
  const blurPx = opts.blurPx ?? 22;
  const landmarks = opts.landmarks || null;

  const srcW = video.videoWidth || 1280;
  const srcH = video.videoHeight || 720;
  const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: false });
  if (!ctx) return { width, height };

  const ellipse = faceEllipseFromLandmarks(landmarks, width, height);
  if (!ellipse) {
    ctx.drawImage(video, 0, 0, width, height);
    return { width, height };
  }

  const sharp = document.createElement('canvas');
  sharp.width = width;
  sharp.height = height;
  const sctx = sharp.getContext('2d', { alpha: false });
  if (!sctx) {
    ctx.drawImage(video, 0, 0, width, height);
    return { width, height };
  }
  sctx.drawImage(video, 0, 0, width, height);

  // Strong background blur via downscale upsample (plus CSS blur when available)
  const blurCanvas = document.createElement('canvas');
  blurCanvas.width = width;
  blurCanvas.height = height;
  const bctx = blurCanvas.getContext('2d', { alpha: false });
  if (!bctx) {
    ctx.drawImage(sharp, 0, 0);
    return { width, height };
  }

  const down = Math.max(0.12, Math.min(0.28, 28 / Math.max(width, height)));
  const dw = Math.max(8, Math.round(width * down));
  const dh = Math.max(8, Math.round(height * down));
  const tiny = document.createElement('canvas');
  tiny.width = dw;
  tiny.height = dh;
  const tctx = tiny.getContext('2d', { alpha: false });
  if (tctx) {
    tctx.drawImage(sharp, 0, 0, dw, dh);
    bctx.imageSmoothingEnabled = true;
    bctx.imageSmoothingQuality = 'high';
    try {
      bctx.filter = `blur(${Math.max(8, Math.round(blurPx * 0.45))}px)`;
    } catch (_) {
      // filter unsupported
    }
    bctx.drawImage(tiny, 0, 0, width, height);
    bctx.filter = 'none';
  } else {
    try {
      bctx.filter = `blur(${blurPx}px)`;
    } catch (_) {
      // ignore
    }
    bctx.drawImage(sharp, 0, 0);
    bctx.filter = 'none';
  }

  const faceMask = makeSoftFaceMask(width, height, ellipse);
  const faceLayer = document.createElement('canvas');
  faceLayer.width = width;
  faceLayer.height = height;
  const fctx = faceLayer.getContext('2d');
  if (!fctx || !faceMask) {
    ctx.drawImage(sharp, 0, 0);
    return { width, height };
  }
  fctx.drawImage(sharp, 0, 0);
  fctx.globalCompositeOperation = 'destination-in';
  fctx.drawImage(faceMask, 0, 0);

  ctx.drawImage(blurCanvas, 0, 0);
  ctx.drawImage(faceLayer, 0, 0);
  return { width, height };
}

/**
 * Capture identity JPEG blob from video with background blur.
 * @returns {Promise<Blob|null>}
 */
export function captureIdentityFaceBlob(canvas, video, opts = {}) {
  const quality = opts.quality ?? 0.8;
  drawIdentityCaptureWithBlur(canvas, video, opts);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', quality);
  });
}
